// 節點分頁（NODE-01）的資料整理：Node Host（KST 或 kinio-node-host）的 shadow kinio_node_host.nodes（SPEC #node-93）。
// 每列：node-id、aid?（節點自己的 AID，讀得到才帶）、port、board、soc、fw、health 四態、last_seen（unix 秒）。
// 列對到節點自己的 Thing 只看 aid：同家戶、使用者看得到（device.read）才連；其他只列不連（不洩漏別處的設備）。
import { can, type VmsDevice } from '../../lib/devices';

export type NodeHealth = 'healthy' | 'busy' | 'unhealthy' | 'unknown';

export interface HostNode {
  id: string;
  aid?: string;
  port?: string;
  board: string;
  soc?: string;
  fw?: string;
  health: NodeHealth;
  /** unix 秒；沒報 = undefined */
  lastSeen?: number;
}

const HEALTH: NodeHealth[] = ['healthy', 'busy', 'unhealthy', 'unknown'];
const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);

/** shadow reported → 節點清單；沒報 kinio_node_host.nodes（不是 Node Host）回 null = 不出現分頁。 */
export function hostNodesOf(reported: Record<string, unknown> | undefined): HostNode[] | null {
  const h = (reported?.kinio_node_host ?? null) as Record<string, unknown> | null;
  if (!h || !Array.isArray(h.nodes)) return null;
  const out: HostNode[] = [];
  for (const x of h.nodes) {
    if (!x || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>;
    const id = str(o.id);
    if (!id) continue;
    out.push({
      id,
      aid: str(o.aid),
      port: str(o.port),
      board: str(o.board) ?? 'unknown',
      soc: str(o.soc),
      fw: str(o.fw),
      health: HEALTH.includes(o.health as NodeHealth) ? (o.health as NodeHealth) : 'unknown',
      lastSeen: typeof o.last_seen === 'number' && o.last_seen > 0 ? o.last_seen : undefined,
    });
  }
  return out.sort((a, b) => a.board.localeCompare(b.board) || a.id.localeCompare(b.id));
}

/** 顯示名：board + node-id 尾 4 碼（SPEC NODE-01）。 */
export function nodeLabel(n: Pick<HostNode, 'board' | 'id'>): string {
  return `${n.board} · ${n.id.slice(-4).toUpperCase()}`;
}

/** aid → 同家戶、看得到的節點 Thing（頂層設備）；沒有就 undefined。 */
export function nodeThing(host: VmsDevice, n: HostNode, tree: VmsDevice[]): VmsDevice | undefined {
  if (!n.aid) return undefined;
  const d = tree.find((x) => x.homeId === host.homeId && x.aid === n.aid && !x.parentAid);
  return d && can(d, 'device.read') ? d : undefined;
}
